/**
 * Emoji avatars: a searchable catalog for the picker, plus validation for any emoji typed from a keyboard.
 * Shared by the web picker and the server so both accept exactly the same values.
 */

/** Longest avatar we store, in UTF-16 code units. The longest real emoji (e.g. 👩🏽‍❤️‍💋‍👨🏻) are about 15. */
export const MAX_EMOJI_LENGTH = 32;

export interface EmojiEntry {
  emoji: string;
  /** Lower-case search words; the first is the display name. */
  keywords: string[];
}

const e = (emoji: string, words: string): EmojiEntry => ({ emoji, keywords: words.split(" ") });

/** Avatar choices, kid favorites first. Search matches the start of any keyword. */
export const EMOJI_CATALOG: EmojiEntry[] = [
  // The original starter set comes first so the default grid looks the same as before.
  e("🦊", "fox animal orange"),
  e("🐼", "panda bear animal"),
  e("🦁", "lion animal cat king"),
  e("🐯", "tiger animal cat"),
  e("🐸", "frog animal green"),
  e("🐙", "octopus animal sea ocean"),
  e("🦄", "unicorn animal horse magic rainbow"),
  e("🐲", "dragon animal fantasy"),
  e("🐵", "monkey animal face"),
  e("🐨", "koala bear animal"),
  e("🐰", "rabbit bunny animal"),
  e("🐶", "dog puppy animal pet"),
  e("🐱", "cat kitten kitty animal pet"),
  e("🦖", "dinosaur trex dino animal"),
  e("🐢", "turtle tortoise animal"),
  e("🦉", "owl bird animal"),
  e("🐝", "bee bug insect honey"),
  e("🦋", "butterfly bug insect"),
  e("🚀", "rocket space ship"),
  e("⚽", "soccer ball football sport"),
  e("🎸", "guitar music rock"),
  e("🎨", "art paint palette"),
  e("🌟", "star glowing sparkle"),
  e("🍕", "pizza food"),
  // Animals
  e("🐻", "bear animal teddy"),
  e("🐻‍❄️", "polar bear animal white"),
  e("🐷", "pig animal"),
  e("🐮", "cow animal"),
  e("🐴", "horse animal pony"),
  e("🐔", "chicken hen animal bird"),
  e("🐧", "penguin bird animal"),
  e("🐦", "bird animal"),
  e("🦅", "eagle bird animal"),
  e("🦜", "parrot bird animal"),
  e("🦩", "flamingo bird animal pink"),
  e("🦆", "duck bird animal"),
  e("🐺", "wolf animal"),
  e("🦝", "raccoon animal"),
  e("🐹", "hamster animal pet"),
  e("🐭", "mouse animal"),
  e("🦔", "hedgehog animal"),
  e("🦦", "otter animal"),
  e("🦥", "sloth animal"),
  e("🦘", "kangaroo animal"),
  e("🦒", "giraffe animal"),
  e("🐘", "elephant animal"),
  e("🦓", "zebra animal"),
  e("🦈", "shark fish sea animal"),
  e("🐬", "dolphin sea animal"),
  e("🐳", "whale sea animal"),
  e("🐠", "fish tropical sea animal"),
  e("🦀", "crab sea animal"),
  e("🐞", "ladybug bug insect"),
  e("🐌", "snail animal"),
  e("🐍", "snake animal"),
  e("🦎", "lizard animal reptile"),
  e("🐊", "crocodile alligator animal"),
  e("🦕", "dinosaur sauropod dino animal"),
  e("🐈‍⬛", "black cat kitten animal pet"),
  e("🐕‍🦺", "service dog animal pet"),
  // Fantasy & people-ish
  e("🤖", "robot bot"),
  e("👽", "alien space"),
  e("👾", "alien monster game space"),
  e("👻", "ghost spooky"),
  e("🧚", "fairy magic"),
  e("🧜", "merperson mermaid magic sea"),
  e("🧙", "wizard mage magic"),
  e("🦸", "superhero hero"),
  e("🥷", "ninja"),
  e("🧞", "genie magic"),
  e("🤠", "cowboy hat face"),
  e("😎", "cool sunglasses face"),
  e("🤓", "nerd glasses face"),
  e("😺", "cat smile face"),
  e("🎃", "pumpkin halloween"),
  e("⛄", "snowman winter snow"),
  // Sports & games
  e("🏀", "basketball ball sport"),
  e("🏈", "football american ball sport"),
  e("⚾", "baseball ball sport"),
  e("🎾", "tennis ball sport"),
  e("🏐", "volleyball ball sport"),
  e("🏓", "ping pong table tennis ball sport"),
  e("🏒", "hockey ice sport"),
  e("🥅", "goal net sport"),
  e("🛹", "skateboard skate"),
  e("🛼", "roller skate skate"),
  e("⛸️", "ice skate skate winter"),
  e("🏄", "surfing surf wave"),
  e("🏊", "swimming swim pool"),
  e("🚴", "bike cycling bicycle"),
  e("🥋", "martial arts karate judo"),
  e("🤸", "gymnastics cartwheel"),
  e("🩰", "ballet dance shoes"),
  e("🎮", "video game controller gaming"),
  e("🕹️", "joystick game arcade"),
  e("🧩", "puzzle piece game"),
  e("♟️", "chess pawn game"),
  e("🎲", "dice game"),
  e("🏆", "trophy winner cup"),
  e("🥇", "gold medal first winner"),
  // Music & art
  e("🎹", "piano keyboard music"),
  e("🥁", "drum music"),
  e("🎺", "trumpet music"),
  e("🎻", "violin music"),
  e("🎤", "microphone sing music karaoke"),
  e("🎧", "headphones music"),
  e("🎬", "movie film clapper"),
  e("📸", "camera photo"),
  e("✏️", "pencil draw write"),
  e("📚", "books reading"),
  e("🔬", "microscope science"),
  e("🧪", "test tube science chemistry"),
  e("🔭", "telescope space science"),
  // Space, nature & weather
  e("⭐", "star"),
  e("✨", "sparkles star magic"),
  e("🌙", "moon crescent night"),
  e("☀️", "sun sunny"),
  e("🌈", "rainbow"),
  e("⚡", "lightning bolt zap"),
  e("🔥", "fire flame hot"),
  e("❄️", "snowflake snow winter"),
  e("🌊", "wave ocean sea water"),
  e("🪐", "planet saturn space"),
  e("🌍", "earth world globe"),
  e("🌻", "sunflower flower"),
  e("🌸", "cherry blossom flower pink"),
  e("🌵", "cactus plant desert"),
  e("🍄", "mushroom"),
  e("🌴", "palm tree beach"),
  e("🍀", "clover four leaf lucky"),
  // Food
  e("🍩", "donut doughnut food"),
  e("🍪", "cookie food"),
  e("🧁", "cupcake cake food"),
  e("🍦", "ice cream food"),
  e("🍓", "strawberry fruit food"),
  e("🍉", "watermelon fruit food"),
  e("🍌", "banana fruit food"),
  e("🍎", "apple fruit food red"),
  e("🥑", "avocado food"),
  e("🌮", "taco food"),
  e("🍔", "burger hamburger food"),
  e("🍟", "fries food"),
  e("🍿", "popcorn food movie"),
  e("🧇", "waffle food"),
  // Things & vehicles
  e("🚗", "car vehicle"),
  e("🏎️", "race car vehicle fast"),
  e("🚂", "train locomotive"),
  e("✈️", "airplane plane"),
  e("🚁", "helicopter"),
  e("⛵", "sailboat boat"),
  e("🚜", "tractor farm"),
  e("🛸", "ufo flying saucer space"),
  e("🎈", "balloon party"),
  e("🎁", "gift present"),
  e("👑", "crown king queen royal"),
  e("💎", "gem diamond"),
  e("🪁", "kite"),
  e("🧸", "teddy bear toy"),
  e("💖", "heart sparkling love pink"),
  e("💙", "heart blue love"),
  e("💚", "heart green love"),
  e("💜", "heart purple love"),
];

/** Catalog entries matching `query` (start of any keyword, or the emoji itself). Empty query → everything. */
export function filterEmoji(query: string, catalog: EmojiEntry[] = EMOJI_CATALOG): EmojiEntry[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return catalog;
  return catalog.filter((entry) => terms.every((t) => entry.emoji === t || entry.keywords.some((k) => k.startsWith(t))));
}

/** The display name for a catalog emoji, if we know it. */
export function emojiName(emoji: string): string | undefined {
  return EMOJI_CATALOG.find((entry) => entry.emoji === emoji)?.keywords[0];
}

// A pictographic base with optional skin tone / presentation selector / tag characters, then optional ZWJ-joined parts.
// Covers ZWJ sequences and subdivision flags, including emoji newer than this runtime's RGI data.
const PICTO_PART = "\\p{Extended_Pictographic}(?:\\p{Emoji_Modifier}|\\uFE0F|[\\u{E0020}-\\u{E007F}])*";
const STRUCTURAL = new RegExp(`^${PICTO_PART}(?:\\u200D${PICTO_PART})*$`, "u");
// Keycaps (1️⃣, #️⃣) and country flags (🇨🇦) aren't pictographic, so match them explicitly.
const KEYCAP_OR_FLAG = /^(?:[0-9#*]️?⃣|\p{Regional_Indicator}{2})$/u;
// Recommended-for-general-interchange emoji, where the runtime supports the `v` flag (Node 20+, modern browsers).
const RGI: RegExp | null = (() => {
  try {
    return new RegExp("^\\p{RGI_Emoji}$", "v");
  } catch {
    return null;
  }
})();

function graphemeCount(s: string): number {
  if (typeof Intl !== "undefined" && "Segmenter" in Intl) {
    return [...new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(s)].length;
  }
  return 1; // The structural patterns already require a single emoji sequence.
}

/**
 * The canonical form of `input` if it is exactly one emoji (one grapheme), else null.
 * Trims whitespace and adds the emoji presentation selector where a keyboard left it off (☺ → ☺️).
 */
export function normalizeEmoji(input: string): string | null {
  const s = input.trim();
  if (!s || s.length > MAX_EMOJI_LENGTH || graphemeCount(s) !== 1) return null;
  if (RGI?.test(s)) return s;
  if (RGI && !s.includes("️") && RGI.test(`${s}️`)) return `${s}️`;
  if (KEYCAP_OR_FLAG.test(s) || STRUCTURAL.test(s)) return s;
  return null;
}

export function isSingleEmoji(input: string): boolean {
  return normalizeEmoji(input) !== null;
}
